import React from "react";
import Select from "react-select";
import { Form, Col, Row } from "react-bootstrap";
import { NumericFormat as NumberFormat } from "react-number-format";

import StateSelect from "../../../layout/select/state";
import { deepEqual } from "../../../utils/object";
import { toNumber } from "../../../utils/string";
import "./tax.css";


const FILING_STATUS = [
    { value: 0, label: "Single" },
    { value: 1, label: "Married Filing Jointly" },
    { value: 2, label: "Married Filing Separately" },
    { value: 3, label: "Head of Household" }
];

class Tax extends React.Component {
    /**
     *
     */
    constructor(props) {
        super(props);
        this.state = this.propsToState(props) 
    }

    componentDidUpdate = (oldProps) => {
        const newSlice = this.propsToState(this.props);
        const oldSlice = this.propsToState(oldProps);

        if (deepEqual(newSlice, oldSlice)) return;

        this.setState(newSlice);
    }

    propsToState = (props) => ({
        state: props.state ?? "VA",
        filing: toNumber(props.filing, 0),
        deduction: toNumber(props.deduction, 0),
        credit: toNumber(props.credit, 0),
        multipleJobs: !!props.multipleJobs,
        extra: toNumber(props.extra, 0),
    });

    onChange = (e) => {
        this.setState({
            [e.target.name]: e.target.value
        }, this.postChange);
    }

    postChange = () => {
        if (this.props.onChange) this.props.onChange(this.state);
    }

    renderMoneyField = ({ id, name, value }) => (
        <div className="withholding-money">
            <span className="withholding-money-prefix">$</span>
            <NumberFormat
                name={name}
                id={id}
                value={value ?? 0}
                thousandSeparator
                allowNegative={false}
                inputMode="decimal"
                decimalScale={2}
                className="form-control withholding-money-control"
                isAllowed={({ floatValue, value: rawValue }) => {
                    if (rawValue === "" || floatValue == null) return true;
                    return floatValue >= 0 && floatValue < 9999999;
                }}
                onValueChange={({ floatValue }) => {
                    this.onChange({ target: { name, value: floatValue ?? 0 } });
                }}
            />
        </div>
    );

    render() {
        if (!this.props.show) return null;

        const tax = this.state || {};
        const status_selected = FILING_STATUS.find((o) => o.value === tax.filing) ?? FILING_STATUS[0];
        const taxYear = new Date().getFullYear();

        return (
            <div className="withholding-form" style={{marginTop: "10px"}}>
                <div className="withholding-header">
                    <div className="withholding-brand">
                        <div className="withholding-brand-small">Payroll</div>
                        <div className="withholding-brand-mark">W-4</div>
                        <div className="withholding-brand-small">Withholding</div>
                    </div>
                    <div className="withholding-title-block">
                        <h2>Employee Withholding Information</h2>
                        <p>Complete this form so payroll can calculate withholding using the employee's current state, filing status, deductions, credits, and additional withholding instructions.</p>
                    </div>
                    <div className="withholding-year">
                        <div className="withholding-year-label">Tax Year</div>
                        <div className="withholding-year-value">{taxYear}</div>
                        <div className="withholding-year-note">Internal payroll worksheet</div>
                    </div>
                </div>

                <section className="withholding-step">
                    <div className="withholding-step-heading">
                        <div className="withholding-step-badge">Step 1</div>
                        <div>
                            <h3>Personal and Filing Information</h3>
                            <p>Select the taxing state and filing status used for payroll withholding.</p>
                        </div>
                    </div>
                    <Row className="g-3">
                        <Col md="6">
                            <Form.Label className="withholding-field-label">1(a) Taxing State</Form.Label>
                            <StateSelect
                                name="state"
                                placeholder=""
                                value={tax.state ?? "VA"}
                                onChange={(value) => {
                                    const next = value?.value ?? value ?? "VA";
                                    this.onChange({ target: { name: "state", value: next }});
                                }}
                            />
                        </Col>
                        <Col md="6">
                            <Form.Label className="withholding-field-label">1(b) Filing Status</Form.Label>
                            <Select
                                value={status_selected}
                                onChange={(e) => this.onChange({ target: { name: "filing", value: e?.value } })}
                                classNamePrefix="react-select"
                                placeholder=""
                                options={FILING_STATUS}
                            />
                        </Col>
                    </Row>
                </section>

                <section className="withholding-step">
                    <div className="withholding-step-heading">
                        <div className="withholding-step-badge">Step 2</div>
                        <div>
                            <h3>Multiple Jobs or Spouse Works</h3>
                            <p>Complete this step when the employee has more than one job or a spouse also works.</p>
                        </div>
                    </div>
                    <div className="withholding-check-row">
                        <Form.Check
                            type="checkbox"
                            id="multipleJobs"
                            checked={!!tax.multipleJobs}
                            onChange={(e) => this.onChange({ target: { name: "multipleJobs", value: e.target.checked } })}
                            label={<span><strong>2(c) Check this box if there is more than one job.</strong><br /><span>This setting may increase withholding to reduce underpayment.</span></span>}
                        />
                    </div>
                </section>

                <section className="withholding-step">
                    <div className="withholding-step-heading">
                        <div className="withholding-step-badge">Step 3</div>
                        <div>
                            <h3>Claim Dependents and Other Credits</h3>
                            <p>Enter annual dependent credits or other credit amounts to reduce withholding.</p>
                        </div>
                    </div>
                    <Row className="g-3 align-items-end">
                        <Col md="8">
                            <Form.Label className="withholding-field-label">3. Annual Dependents Credit</Form.Label>
                            <div className="withholding-help">Example: qualifying children, other dependents, or other credits claimed for withholding purposes.</div>
                        </Col>
                        <Col md="4">
                            {this.renderMoneyField({ id: "credit", name: "credit", value: tax?.credit })}
                        </Col>
                    </Row>
                </section>

                <section className="withholding-step">
                    <div className="withholding-step-heading">
                        <div className="withholding-step-badge">Step 4</div>
                        <div>
                            <h3>Other Adjustments</h3>
                            <p>Use these fields for annual deductions and additional withholding.</p>
                        </div>
                    </div>
                    <Row className="g-3">
                        <Col md="6">
                            <Form.Label className="withholding-field-label">4(b) Annual Deduction</Form.Label>
                            {this.renderMoneyField({ id: "deduction", name: "deduction", value: tax?.deduction })}
                        </Col>
                        <Col md="6">
                            <Form.Label className="withholding-field-label">4(c) Extra Withholding</Form.Label>
                            {this.renderMoneyField({ id: "extra", name: "extra", value: tax?.extra })}
                        </Col>
                    </Row>
                </section>
            </div>
        )
    }
}

export default Tax;
